import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Championship } from 'modules/tournaments/domain/interfaces/responce-dto';
import { TourneyStatus } from 'modules/tournaments/domain/enums/tourney-status';
import UDHeaderTitle from 'ud-ui/components/header-title';
import UDPagination from 'ud-ui/components/pagination';
import { useMainLayout } from 'ud-ui/layout/main/context';

import { useChampionshipsList } from '../hooks/use-championships-list';

const ChampionshipsListPage: React.FC = () => {
  const { t } = useTranslation();
  const { setHeaderContent } = useMainLayout();

  const {
    items,
    isLoading,
    isError,
    error,
    page,
    limit,
    totalPages,
    filters,
    setPage,
    setFilters,
    setPerPage,
  } = useChampionshipsList();

  const [searchInput, setSearchInput] = useState(filters.namePart ?? '');

  useEffect(() => {
    setHeaderContent(<UDHeaderTitle>Чемпионаты (тест useOffsetPagination)</UDHeaderTitle>);
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchInput(value);
      setFilters({ namePart: value });
    },
    [setFilters],
  );

  const handlePerPageChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setPerPage(Number(e.target.value));
    },
    [setPerPage],
  );

  const listNode = useMemo(() => {
    if (isLoading) {
      return <div className="p-6 text-center">Загрузка...</div>;
    }

    if (isError) {
      return <div className="p-6 text-center text-danger">Ошибка: {String(error)}</div>;
    }

    if (items.length === 0) {
      return <div className="p-6 text-center">Нет данных</div>;
    }

    return (
      <div className="flex flex-col gap-2 px-6">
        {items.map((championship: Championship) => (
          <div
            key={championship.id}
            className="flex items-center justify-between p-4 bg-white rounded"
            style={{ border: '1px solid #e0e0e0' }}
          >
            <div>
              <div className="fw-bold">{championship.name}</div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                {championship.startDate && `Начало: ${championship.startDate}`}
                {championship.endDate && ` — Конец: ${championship.endDate}`}
              </div>
              {championship.locations?.[0]?.address?.city && (
                <div className="text-muted" style={{ fontSize: 13 }}>
                  {championship.locations[0].address.city}
                </div>
              )}
            </div>
            {championship.status && (
              <span
                className="px-3 py-1 rounded"
                style={{
                  fontSize: 12,
                  backgroundColor: championship.status === TourneyStatus.OPEN ? '#d4edda' : '#f8d7da',
                  color: championship.status === TourneyStatus.OPEN ? '#155724' : '#721c24',
                }}
              >
                {championship.status === TourneyStatus.OPEN ? 'Прием заявок открыт' : 'Прием заявок закрыт'}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  }, [isLoading, isError, error, items]);

  return (
    <div className="w-full px-6 py-6">
      <div className="flex items-center gap-4 px-6 mb-4">
        <input
          type="text"
          className="form-control"
          placeholder="Поиск по названию..."
          value={searchInput}
          onChange={handleSearchChange}
          style={{ maxWidth: 300 }}
        />
        <select className="form-select" value={limit} onChange={handlePerPageChange} style={{ maxWidth: 120 }}>
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
        <span className="text-muted" style={{ fontSize: 13 }}>
          Страница {page} / {totalPages || '—'}
        </span>
      </div>

      {listNode}

      {totalPages > 1 && (
        <UDPagination
          className="justify-end my-4 px-6"
          pages={totalPages}
          currentPage={page}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};

export default ChampionshipsListPage;
